// File validation utilities for SortingCat AI

export const FILE_SIZE_LIMIT = 10 * 1024 * 1024; // 10 MB in bytes

export interface FileValidation {
  isValid: boolean;
  reason?: 'size_exceeded' | 'unsupported_format';
  maxSize?: number;
}

/**
 * Validates a file's size against the 10 MB limit
 * @param file - The file to validate
 * @returns FileValidation object indicating if file is valid
 */
export const validateFileSize = (file: File): FileValidation => {
  if (file.size >= FILE_SIZE_LIMIT) {
    return {
      isValid: false,
      reason: 'size_exceeded',
      maxSize: FILE_SIZE_LIMIT
    };
  }
  return { isValid: true };
};

/**
 * Partitions files into valid and invalid arrays
 * @param files - Array of file previews with validation
 * @returns Object with valid and invalid file arrays
 */
export const partitionFiles = <T extends { validation: FileValidation }>(
  files: T[]
): { valid: T[]; invalid: T[] } => {
  const valid: T[] = [];
  const invalid: T[] = [];

  files.forEach(file => {
    if (file.validation.isValid) {
      valid.push(file);
    } else {
      invalid.push(file);
    }
  });

  return { valid, invalid };
};

/**
 * Sorts files with invalid files at the top, maintaining selection order within each group
 * @param files - Array of file previews with validation
 * @returns Sorted array with invalid files first
 */
export const sortFilesInvalidFirst = <T extends { validation: FileValidation }>(
  files: T[]
): T[] => {
  const { valid, invalid } = partitionFiles(files);
  // Invalid files at top, then valid files
  // Both groups maintain their original selection order (Decision #4: Option C)
  return [...invalid, ...valid];
};

/**
 * Formats file size in a human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Gets a playful "Sorting Cat" message based on invalid file count
 * @param count - Number of invalid files
 * @returns Playful message string
 */
export const getSizeLimitMessage = (count: number): string => {
  const messages = [
    "Meow! This file is too big for my sorting paws! 🐾",
    "Whoa! These files are TOO LARGE! Even for a big cat like me! 😺",
    "My whiskers are tingling! Those files are getting hefty! 📏",
    "Purrfect files are under 10 MB! These are a bit too fluffy! 🎯"
  ];

  if (count === 1) {
    return messages[0];
  } else if (count <= 3) {
    return messages[1];
  } else if (count <= 5) {
    return messages[2];
  } else {
    return messages[3];
  }
};
